import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, CheckCircle, XCircle, Mail, Phone, MessageSquare, MapPin,Clock ,Award,Users  } from 'lucide-react';
import { apiService } from '../services/apiService';

const Contact = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    asunto: '',
    mensaje: ''
  });
  const [errors, setErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState(null); // null, 'success', 'error'
  const [loading, setLoading] = useState(false);

  // Validación de campos
  const validate = () => {
    const newErrors = {};
    if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es obligatorio';
    if (!formData.email.trim()) {
      newErrors.email = 'El email es obligatorio';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    if (!formData.mensaje.trim()) newErrors.mensaje = 'El mensaje es obligatorio';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Limpiar error al editar
    if (errors[name]) {
      setErrors(prev => {
        const { [name]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setLoading(true);
      try {
        const response = await apiService.sendContactMessage(formData);
        if (response.estado === 1) {
          setSubmitStatus('success');
          // Reset form after success
          setFormData({
            nombre: '',
            email: '',
            telefono: '',
            asunto: '',
            mensaje: ''
          });
          // Reset status after 5 seconds
          setTimeout(() => setSubmitStatus(null), 5000);
        } else {
          setSubmitStatus('error');
        }
      } catch (error) {
        setSubmitStatus('error');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pt-28 pb-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* --- ENCABEZADO --- */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600">Contáctanos</span>
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-yellow-500 to-transparent mx-auto mb-6 rounded-full" />
          <p className="text-gray-400 text-lg md:text-xl leading-relaxed">
            ¿Tienes preguntas? Queremos ayudarte a planificar tu próximo evento con éxito.
          </p>
        </motion.div>

        {/* --- CONTENIDO PRINCIPAL --- */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          
          {/* Columna Izquierda: Formulario */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Mensaje de éxito/error */}
            <AnimatePresence>
              {submitStatus && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className={`mb-8 p-4 rounded-xl flex items-center gap-3 ${
                    submitStatus === 'success' 
                      ? 'bg-green-900/50 border border-green-700/50 text-green-400' 
                      : 'bg-red-900/50 border border-red-700/50 text-red-400'
                  }`}
                >
                  {submitStatus === 'success' ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <XCircle className="w-5 h-5" />
                  )}
                  <span>
                    {submitStatus === 'success' 
                      ? '¡Mensaje enviado con éxito! Nos pondremos en contacto contigo pronto.' 
                      : 'Hubo un error al enviar tu mensaje. Por favor, intenta de nuevo.'}
                  </span>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="bg-neutral-900/40 border border-yellow-600/20 rounded-2xl p-8">
              <form onSubmit={handleSubmit}>
                
                {/* Nombre */}
                <div className="mb-6">
                  <label htmlFor="nombre" className="block text-sm font-medium text-gray-400 mb-2">
                    Nombre <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="nombre"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleChange}
                      className={`w-full bg-neutral-900 border ${errors.nombre ? 'border-red-500' : 'border-yellow-600/20'} rounded-lg px-4 py-3 focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 transition-all duration-300`}
                      placeholder="Tu nombre completo"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Mail className="w-4 h-4 text-yellow-500" />
                    </div>
                  </div>
                  {errors.nombre && (
                    <p className="text-red-400 text-sm mt-1">{errors.nombre}</p>
                  )}
                </div>

                {/* Email */}
                <div className="mb-6">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full bg-neutral-900 border ${errors.email ? 'border-red-500' : 'border-yellow-600/20'} rounded-lg px-4 py-3 focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 transition-all duration-300`}
                      placeholder="tu@correo.com"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Mail className="w-4 h-4 text-yellow-500" />
                    </div>
                  </div>
                  {errors.email && (
                    <p className="text-red-400 text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                {/* Teléfono */}
                <div className="mb-6">
                  <label htmlFor="telefono" className="block text-sm font-medium text-gray-400 mb-2">
                    Teléfono
                  </label>
                  <div className="relative">
                    <input
                      type="tel"
                      id="telefono"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleChange}
                      className="w-full bg-neutral-900 border border-yellow-600/20 rounded-lg px-4 py-3 focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 transition-all duration-300"
                      placeholder="+51 999 888 777"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Phone className="w-4 h-4 text-yellow-500" />
                    </div>
                  </div>
                </div>

                {/* Asunto - Desplegable */}
                <div className="mb-6">
                  <label htmlFor="asunto" className="block text-sm font-medium text-gray-400 mb-2">
                    Asunto
                  </label>
                  <div className="relative">
                    <select
                      id="asunto"
                      name="asunto"
                      value={formData.asunto}
                      onChange={handleChange}
                      className="w-full bg-neutral-900 border border-yellow-600/20 rounded-lg px-4 py-3 focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 transition-all duration-300 appearance-none cursor-pointer text-white"
                    >
                      <option value="" className="bg-neutral-900">Selecciona un tipo de evento</option>
                      <option value="CONCIERTO" className="bg-neutral-900">CONCIERTO</option>
                      <option value="BODA" className="bg-neutral-900">BODA</option>
                      <option value="QUINCEAÑOS" className="bg-neutral-900">QUINCEAÑOS</option>
                      <option value="EVENTO CORPORATIVO" className="bg-neutral-900">EVENTO CORPORATIVO</option>
                      <option value="OTRO" className="bg-neutral-900">OTRO</option>
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <MessageSquare className="w-4 h-4 text-yellow-500" />
                    </div>
                  </div>
                </div>

                {/* Mensaje */}
                <div className="mb-8">
                  <label htmlFor="mensaje" className="block text-sm font-medium text-gray-400 mb-2">
                    Mensaje <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <textarea
                      id="mensaje"
                      name="mensaje"
                      rows="6"
                      value={formData.mensaje}
                      onChange={handleChange}
                      className={`w-full bg-neutral-900 border ${errors.mensaje ? 'border-red-500' : 'border-yellow-600/20'} rounded-lg px-4 py-3 focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 transition-all duration-300`}
                      placeholder="Escribe tu mensaje aquí..."
                    />
                    <div className="absolute right-3 bottom-3">
                      <MessageSquare className="w-4 h-4 text-yellow-500" />
                    </div>
                  </div>
                  {errors.mensaje && (
                    <p className="text-red-400 text-sm mt-1">{errors.mensaje}</p>
                  )}
                </div>

                {/* Botón Enviar */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 text-black font-bold text-center px-8 py-4 rounded-full hover:from-yellow-400 hover:to-yellow-500 transition-all duration-300 shadow-lg hover:shadow-yellow-500/25 disabled:opacity-70"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                      <span>Enviando...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <Send className="w-4 h-4" />
                      <span>Enviar Mensaje</span>
                    </div>
                  )}
                </button>
              </form>
            </div>
          </motion.div>

          {/* Columna Derecha: Información de Contacto */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-neutral-900/40 border border-yellow-600/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-white mb-6">Información de Contacto</h2>
              
              {/* Dirección */}
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-black" />
                </div>
                <div>
                  <h3 className="text-white font-medium mb-1">Dirección</h3>
                  <p className="text-gray-400">Calle Diego Ferre Nro. 375 Int. 401, Miraflores</p>
                </div>
              </div>

              {/* Teléfono */}
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-black" />
                </div>
                <div>
                  <h3 className="text-white font-medium mb-1">Teléfono</h3>
                  <p className="text-gray-400">+51 955 000 321</p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-black" />
                </div>
                <div>
                  <h3 className="text-white font-medium mb-1">Email</h3>
                  <p className="text-gray-400">ventas@ROTMAS.com</p>
                </div>
              </div>

              {/* Horario */}
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-black" />
                </div>
                <div>
                  <h3 className="text-white font-medium mb-1">Horario de Atención</h3>
                  <p className="text-gray-400">Lunes a Viernes: 9:00 AM - 6:00 PM</p>
                </div>
              </div>

              {/* Mapa */}
              <div className="mt-12 rounded-xl overflow-hidden border border-yellow-600/20">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d15407.860492566058!2d-77.02998539999999!3d-12.1083947!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x9105c5c6e7c1d1b5%3A0x26b9b8e3f4e3a8d9!2sMiraflores%2C%20Lima!5e0!3m2!1ses!2spe!4v1699998857460!5m2!1ses!2spe"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Ubicación de ROTMANS"
                ></iframe>
              </div>
            </div>
          </motion.div>
        </div>

        {/* --- SECCIÓN DE VALORES --- */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mt-20"
        >
          <div className="text-center mb-12">
            <span className="text-yellow-500 text-sm font-semibold tracking-widest uppercase border-b border-yellow-600/50 pb-1">
              Por qué confiar en nosotros
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              {
                icon: CheckCircle,
                titulo: 'Respuesta Rápida',
                descripcion: 'Contestamos en menos de 24 horas',
                color: 'from-yellow-400 to-yellow-600'
              },
              {
                icon: Award,
                titulo: 'Experiencia',
                descripcion: '+10 años en la industria',
                color: 'from-yellow-400 to-yellow-600'
              },
              {
                icon: Users,
                titulo: 'Equipo Profesional',
                descripcion: 'Expertos en audio, luces y pantallas',
                color: 'from-yellow-400 to-yellow-600'
              }
            ].map((item, index) => (
              <motion.div
                key={item.titulo}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="bg-neutral-900/40 border border-yellow-600/20 rounded-2xl p-8 text-center hover:border-yellow-500/50 transition-all duration-500 hover:shadow-[0_10px_40px_-15px_rgba(234,179,8,0.15)]"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-yellow-500/10 to-yellow-600/10 border border-yellow-600/30 flex items-center justify-center">
                  <item.icon className="w-7 h-7 text-yellow-500" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{item.titulo}</h3>
                <p className="text-gray-400 text-sm">{item.descripcion}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;